import type {
  Component,
  DataEntryComponent,
  PageTemplate,
  PageTemplateComponent,
  PageTemplateConfig,
  PageTemplateInput,
} from '../../../../api/pages-client/pages-client.type';
import { ObjectType } from './dashboard-template.type';

/**
 * Converts a page template into a list of Components by mapping object keys to actual object UUIDs
 *
 * @param {PageTemplate | null | undefined} pageTemplate - The page template to convert
 * @param {Record<string, string>} objectKeyToUuidMap - Dictionary mapping object keys to actual object UUIDs
 * @param {string} pageId - The ID of the page these components will belong to
 * @returns {Component[]} Array of Components with object_ids mapped to actual UUIDs
 */
function convertPageTemplateToComponents(
  pageTemplate: PageTemplate | null | undefined,
  objectKeyToUuidMap: Record<string, string>,
  pageId: string,
): Component[] {
  if (!pageTemplate?.config?.components) {
    return [];
  }

  return pageTemplate.config.components.map((templateComponent: PageTemplateComponent): Component => {
    // Convert PageTemplateComponent to Component by adding the page field
    const component: Component = {
      ...templateComponent,
      page: pageId,
    };

    // Map object_ids from keys to actual UUIDs in component_data
    if (component.component_data) {
      component.component_data = component.component_data.map((data) => {
        // Map object_ids from template keys to actual UUIDs
        const mappedObjectIds = data.object_ids.map((objectKey) => {
          const actualUuid = objectKeyToUuidMap[objectKey];
          if (!actualUuid) {
            return objectKey; // Return original key if no mapping found
          }
          return actualUuid;
        });

        // Update color_map to use actual UUIDs instead of keys
        let mappedColorMap = data.color_map;
        if (data.color_map) {
          mappedColorMap = {};
          for (const [colorKey, colorValue] of Object.entries(data.color_map)) {
            const actualUuid = objectKeyToUuidMap[colorKey];
            if (actualUuid) {
              mappedColorMap[actualUuid] = colorValue;
            } else {
              // If no mapping found, keep the original key but warn
              mappedColorMap[colorKey] = colorValue;
            }
          }
        }

        return {
          ...data,
          object_ids: mappedObjectIds,
          color_map: mappedColorMap,
        };
      });
    }

    return component;
  });
}

/**
 * Validates that all required object keys have mappings in the provided dictionary
 *
 * @param {PageTemplate | null | undefined} pageTemplate - The page template to validate
 * @param {Record<string, string>} objectKeyToUuidMap - Dictionary mapping object keys to actual object UUIDs
 * @returns {Object} Object with validation result and any missing keys
 */
function validateTemplateObjectMappings(
  pageTemplate: PageTemplate | null | undefined,
  objectKeyToUuidMap: Record<string, string>,
): { isValid: boolean; missingKeys: string[] } {
  const missingKeys: string[] = [];

  if (!pageTemplate?.config?.components) {
    return { isValid: true, missingKeys: [] };
  }

  // Collect all object keys used in the template
  const allObjectKeys = new Set<string>();

  pageTemplate.config.components.forEach((component) => {
    component.component_data?.forEach((data) => {
      data.object_ids.forEach((objectKey) => {
        allObjectKeys.add(objectKey);
      });
    });
  });

  // Check if all keys have mappings
  allObjectKeys.forEach((key) => {
    if (!objectKeyToUuidMap[key]) {
      missingKeys.push(key);
    }
  });

  return {
    isValid: missingKeys.length === 0,
    missingKeys,
  };
}

/**
 * Extracts all unique object keys used in a page template
 * This is useful for determining what mappings are needed
 *
 * @param {PageTemplate | null | undefined} pageTemplate - The page template to analyze
 * @returns {string[]} Array of unique object keys used in the template
 */
function extractTemplateObjectKeys(pageTemplate: PageTemplate | null | undefined): string[] {
  if (!pageTemplate?.config?.components) {
    return [];
  }

  const objectKeys = new Set<string>();

  for (const component of pageTemplate.config.components) {
    if (!component.component_data) continue;

    for (const data of component.component_data) {
      // Add all object IDs at once
      if (data.object_ids) {
        data.object_ids.forEach((key) => {
          objectKeys.add(key);
        });
      }
    }
  }

  return Array.from(objectKeys).sort();
}

/**
 * Converts an object type to a human readable string
 *
 * @param {string} object_type - The object type to convert
 * @returns {string} The human readable string
 */
function getTemplateObjectType(object_type: ObjectType): string {
  if (object_type === ObjectType.Sprint) {
    return 'Board';
  }
  return object_type.charAt(0).toUpperCase() + object_type.slice(1);
}

interface TemplateKeyResolverState {
  uuidToKey: Record<string, string>;
  existingKeys: Set<string>;
  inputs: PageTemplateInput[];
  nextCount: number;
}

/**
 * Resolves a real UUID to a template object key, minting a new `object_N`
 * key (and a matching input entry) when no mapping exists yet. Mutates
 * `state` so repeated calls share the same key namespace and input list.
 *
 * @param {TemplateKeyResolverState} state - Mutable resolver state (uuid->key map, used keys, inputs, counter)
 * @param {string} uuid - Real UUID to resolve to a template key
 * @param {string} objectType - Object type recorded on a newly-minted input entry
 * @returns {string} The existing or newly-generated template key for `uuid`
 */
function resolveKeyForUuid(state: TemplateKeyResolverState, uuid: string, objectType: string): string {
  if (state.uuidToKey[uuid]) return state.uuidToKey[uuid];
  let key = `object_${state.nextCount}`;
  while (state.existingKeys.has(key)) {
    state.nextCount += 1;
    key = `object_${state.nextCount}`;
  }
  state.uuidToKey[uuid] = key;
  state.existingKeys.add(key);
  state.inputs.push({ key, object_type: objectType });
  state.nextCount += 1;
  return key;
}

/**
 * Converts live components (with real UUIDs) and data entry components back
 * into a PageTemplateConfig (with template object keys). Used when saving
 * edits made on a template's preview back to the template itself.
 *
 * Real UUIDs present in `objectKeyToUuidMap` are swapped for their existing
 * template keys. Real UUIDs not present get a freshly-generated `object_N`
 * key and a new input entry so the template remains fully parameterized.
 *
 * @param {Component[]} components - Live components rendered on the template (with real UUIDs)
 * @param {DataEntryComponent[]} dataEntryComponents - Live data entry components
 * @param {Record<string, string>} objectKeyToUuidMap - Forward mapping used during Apply (template key -> real UUID)
 * @param {PageTemplateInput[]} existingInputs - Inputs from the template's current config (preserved when possible)
 * @returns {PageTemplateConfig} A PageTemplateConfig ready to PUT to /api/page-templates/{id}/
 */
function convertComponentsToTemplateConfig(
  components: Component[],
  dataEntryComponents: DataEntryComponent[],
  objectKeyToUuidMap: Record<string, string>,
  existingInputs: PageTemplateInput[],
): PageTemplateConfig {
  const uuidToKey: Record<string, string> = Object.fromEntries(
    Object.entries(objectKeyToUuidMap).map(([key, uuid]) => [uuid, key]),
  );

  const inputs: PageTemplateInput[] = existingInputs.map((input) => ({ ...input }));
  const existingKeys = new Set(inputs.map((input) => input.key));
  const resolverState: TemplateKeyResolverState = {
    uuidToKey,
    existingKeys,
    inputs,
    nextCount: inputs.length,
  };

  const templateComponents: PageTemplateComponent[] = components.map((component) => {
    const componentData = (component.component_data || []).map((data) => {
      const mappedObjectIds = (data.object_ids || []).map((objectId) =>
        resolveKeyForUuid(resolverState, objectId, data.object_type),
      );
      const mappedColorMap: Record<string, string> = {};
      for (const [colorKey, colorValue] of Object.entries(data.color_map || {})) {
        const resolved = uuidToKey[colorKey] ?? resolveKeyForUuid(resolverState, colorKey, data.object_type);
        mappedColorMap[resolved] = colorValue;
      }
      return {
        ...data,
        object_ids: mappedObjectIds,
        color_map: mappedColorMap,
      };
    });

    return {
      name: component.name,
      description: component.description,
      type: component.type,
      sub_type: component.sub_type,
      show_title: component.show_title,
      layout_data: component.layout_data,
      component_data: componentData,
    };
  });

  const templateDataEntryComponents: DataEntryComponent[] = dataEntryComponents.map((component) => ({
    ...component,
    id: undefined,
    page: '',
    stored_data: [],
  }));

  const referencedKeys = new Set<string>(
    templateComponents.flatMap((comp) =>
      (comp.component_data || []).flatMap((data) => [...(data.object_ids || []), ...Object.keys(data.color_map || {})]),
    ),
  );
  const prunedInputs = inputs.filter((input) => referencedKeys.has(input.key));

  return {
    inputs: prunedInputs,
    components: templateComponents,
    data_entry_components: templateDataEntryComponents,
  };
}

export {
  convertComponentsToTemplateConfig,
  convertPageTemplateToComponents,
  extractTemplateObjectKeys,
  getTemplateObjectType,
  validateTemplateObjectMappings,
};
